import { SERVER_VERSION_FETCH_TIMEOUT_MS, WARM_BOOT_SCOPE } from "../config/swConfig";
import { recordApiCall } from "../sync/apiTrace"; // v3.2.08

let cachedServerVersionResult = null;
let cachedServerVersionAt = 0;
let versionFetchBackoffUntil = 0;
let lastWarmRequestAt = 0;
let lastWarmRequestKey = "";

async function _fetchWithTimeout(url, init = {}, timeoutMs = SERVER_VERSION_FETCH_TIMEOUT_MS) {
  const __ts = Date.now();
  try {
    const res = await Promise.race([
      fetch(url, init),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), timeoutMs)),
    ]);
    try {
      recordApiCall({ url: String(url || ""), action: "", source: "swRuntime.versionCheck",
        status: res?.status || 0, durMs: Date.now() - __ts,
        result: res?.status === 429 ? "real_429"
          : res?.status === 503 ? "real_503"
          : (res?.ok ? "ok" : "error") });
    } catch (_) {}
    return res;
  } catch (err) {
    try {
      recordApiCall({ url: String(url || ""), action: "", source: "swRuntime.versionCheck",
        status: 0, durMs: Date.now() - __ts,
        result: String(err?.message || "").includes("timeout") ? "timeout" : "network_error" });
    } catch (_) {}
    throw err;
  }
}

export async function fetchServerVersion(options = {}) {
  const basePath = String(options.basePath || "");
  const now = Date.now();
  const minIntervalMs = Number(options.minIntervalMs || 60000);
  if (cachedServerVersionResult && (now - cachedServerVersionAt) < minIntervalMs) return cachedServerVersionResult;
  if (versionFetchBackoffUntil > now) return cachedServerVersionResult || null;
  const stamp = Math.floor(now / 60000);
  try {
    const res = await _fetchWithTimeout(`${basePath}/version.json?t=${now}`, { cache: "no-store" }, SERVER_VERSION_FETCH_TIMEOUT_MS);
    if (res?.ok) {
      const data = await res.json();
      cachedServerVersionResult = data?.version || null;
      cachedServerVersionAt = now;
      return cachedServerVersionResult;
    }
    if (res?.status === 429 || res?.status === 503) { versionFetchBackoffUntil = now + (res.status === 429 ? 180000 : 120000); return cachedServerVersionResult || null; }
  } catch (e) {}
  try {
    const res = await _fetchWithTimeout(`${basePath}/index.html?t=${stamp}`, { cache: "no-cache" }, SERVER_VERSION_FETCH_TIMEOUT_MS);
    if (res?.status === 429 || res?.status === 503) { versionFetchBackoffUntil = now + 180000; return cachedServerVersionResult || null; }
    const html = await res.text();
    const m = html.match(/APP_VERSION\s*=\s*['"]([^'"]+)['"]/);
    if (m) {
      cachedServerVersionResult = m[1];
      cachedServerVersionAt = now;
      return cachedServerVersionResult;
    }
  } catch (e) {}
  return cachedServerVersionResult || null;
}


export async function requestWarmScope(scope = "home", reason = "warm") {
  try {
    const now = Date.now();
    const key = String(scope || "home") + "::" + String(reason || "warm");
    if (key === lastWarmRequestKey && now - lastWarmRequestAt < 45000) return;
    lastWarmRequestKey = key;
    lastWarmRequestAt = now;
    const reg = await navigator.serviceWorker?.getRegistration?.();
    const payload = { type: "WARM_CACHE", scope, reason };
    reg?.active?.postMessage?.(payload);
    navigator.serviceWorker?.controller?.postMessage?.(payload);
  } catch (e) {}
}

export async function requestHomeWarmCache(reason = "home") {
  await requestWarmScope("home", reason);
}

export async function requestShellWarmCache(reason = "shell") {
  await requestWarmScope("shell", reason);
}

export async function requestPriorityWarmCache(reason = "priority") {
  await requestWarmScope("priority", reason);
}

export async function requestAdaptiveWarmCache(reason = "adaptive") {
  await requestWarmScope("adaptive", reason);
}

export async function registerBootServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all((regs || []).map((reg) => {
      try {
        const scriptURL = reg?.active?.scriptURL || reg?.waiting?.scriptURL || reg?.installing?.scriptURL || "";
        if (scriptURL.includes('/service-worker.js?')) return reg.unregister?.();
      } catch (e) {}
      return Promise.resolve();
    }));
  } catch (e) {}
  try {
    const reg = await navigator.serviceWorker.register(`/service-worker.js`, { updateViaCache: "none" });
    try {
      reg?.active?.postMessage?.({ type: "WARM_CACHE", scope: WARM_BOOT_SCOPE });
      navigator.serviceWorker?.controller?.postMessage?.({ type: "WARM_CACHE", scope: WARM_BOOT_SCOPE });
    } catch (e) {}
    return reg;
  } catch (e) {
    return null;
  }
}
